import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import {
  MoreHorizontal,
  Search,
  Filter,
  Pin,
  Lock,
  Trash2,
  MessageCircle,
  ThumbsUp,
  Eye,
  CheckCircle2,
  Unlock,
  PinOff,
  BookOpen,
  FileText,
  ArrowUpDown,
  ExternalLink,
  Calendar,
} from "lucide-react";
import { TableActionMenu } from "../components/ui/TableActionMenu";
import { useToast } from "../components/ui/ToastContainer";
import { discussionService } from "../services/discussionService";
import { courseService } from "../services/courseService";
import { format } from "date-fns";

// Types
interface Discussion {
  _id: string;
  title: string;
  content: string;
  author: {
    _id: string;
    username: string;
    firstName: string;
    lastName: string;
    profilePicture?: string;
  };
  course: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  lesson?: {
    _id: string;
    title: string;
    order: number;
  };
  isPinned: boolean;
  isLocked: boolean;
  isSolved: boolean;
  upvotes: any[];
  replies: any[];
  views: number;
  createdAt: string;
  updatedAt: string;
}

interface CourseOption {
  _id: string;
  title: string;
}

export default function DiscussionsPage() {
  const { showSuccess, showError } = useToast();
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters & Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [courseFilter, setCourseFilter] = useState("all");
  const [pinnedFilter, setPinnedFilter] = useState("all");
  const [lockedFilter, setLockedFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [courseSearchTerm, setCourseSearchTerm] = useState("");

  // Local UI State
  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isCourseFilterOpen, setIsCourseFilterOpen] = useState(false);
  const [isPinnedFilterOpen, setIsPinnedFilterOpen] = useState(false);
  const [isLockedFilterOpen, setIsLockedFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] =
    useState<Discussion | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  const sortOptions = [
    { label: "Most Recent", value: "recent" },
    { label: "Most Popular", value: "popular" },
    { label: "Unanswered", value: "unanswered" },
    { label: "Solved", value: "solved" },
    { label: "Oldest", value: "oldest" },
  ];

  const statusOptions = [
    { label: "All Status", value: "all" },
    { label: "Solved", value: "solved" },
    { label: "Unsolved", value: "unsolved" },
  ];

  const pinnedOptions = [
    { label: "All", value: "all" },
    { label: "Pinned Only", value: "pinned" },
    { label: "Not Pinned", value: "unpinned" },
  ];

  const lockedOptions = [
    { label: "All", value: "all" },
    { label: "Locked Only", value: "locked" },
    { label: "Unlocked", value: "unlocked" },
  ];

  // Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchDiscussions = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: any = {
        page: currentPage,
        limit: pageSize,
        sortBy,
        search: debouncedSearch,
      };

      if (statusFilter === "solved") params.isSolved = "true";
      if (statusFilter === "unsolved") params.isSolved = "false";
      if (courseFilter !== "all") params.course = courseFilter;
      if (pinnedFilter === "pinned") params.isPinned = "true";
      if (pinnedFilter === "unpinned") params.isPinned = "false";
      if (lockedFilter === "locked") params.isLocked = "true";
      if (lockedFilter === "unlocked") params.isLocked = "false";

      const response = (await discussionService.getAllDiscussions(
        params,
      )) as any;

      if (response && response.discussions) {
        setDiscussions(response.discussions);
        setTotalItems(response.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch discussions:", error);
      showError("Failed to load discussions");
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    sortBy,
    debouncedSearch,
    statusFilter,
    courseFilter,
    pinnedFilter,
    lockedFilter,
    showError,
  ]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response: any = await courseService.getAllCourses({
          limit: 1000,
          isPublished: "all",
        });
        if (response && response.data && response.data.courses) {
          setCourses(
            response.data.courses.map((c: any) => ({
              _id: c._id,
              title: c.title,
            })),
          );
        }
      } catch (error) {
        console.error("Failed to fetch courses:", error);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    fetchDiscussions();
  }, [fetchDiscussions]);

  // Actions
  const handleTogglePin = async (discussion: Discussion) => {
    try {
      await discussionService.togglePinDiscussion(discussion._id);
      showSuccess(
        discussion.isPinned ? "Discussion unpinned" : "Discussion pinned",
      );
      fetchDiscussions();
    } catch (error) {
      showError("Failed to update pin status");
    }
    setActiveMenu(null);
  };

  const handleToggleLock = async (discussion: Discussion) => {
    try {
      await discussionService.toggleLockDiscussion(discussion._id);
      showSuccess(
        discussion.isLocked ? "Discussion unlocked" : "Discussion locked",
      );
      fetchDiscussions();
    } catch (error) {
      showError("Failed to update lock status");
    }
    setActiveMenu(null);
  };

  const handleToggleSolved = async (discussion: Discussion) => {
    try {
      await discussionService.toggleSolvedDiscussion(discussion._id);
      showSuccess(
        discussion.isSolved
          ? "Discussion marked as unsolved"
          : "Discussion marked as solved",
      );
      fetchDiscussions();
      if (selectedDiscussion?._id === discussion._id) {
        setSelectedDiscussion({
          ...selectedDiscussion,
          isSolved: !selectedDiscussion.isSolved,
        });
      }
    } catch (error) {
      showError("Failed to update solved status");
    }
    setActiveMenu(null);
  };

  const handleDelete = async (discussionId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this discussion? This action cannot be undone.",
      )
    )
      return;

    try {
      await discussionService.deleteDiscussion(discussionId);
      showSuccess("Discussion deleted successfully");
      fetchDiscussions();
    } catch (error) {
      showError("Failed to delete discussion");
    }
    setActiveMenu(null);
  };

  const handleViewDetails = async (discussionId: string) => {
    try {
      setIsLoadingDetails(true);
      setIsModalOpen(true);
      const response = (await discussionService.getDiscussionById(
        discussionId,
      )) as any;
      if (response && response.discussion) {
        setSelectedDiscussion(response.discussion);
      }
    } catch (error) {
      console.error("Failed to fetch discussion details:", error);
      showError("Failed to load discussion details");
      setIsModalOpen(false);
    } finally {
      setIsLoadingDetails(false);
    }
    setActiveMenu(null);
  };

  const handleAddReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDiscussion || !replyContent.trim() || isSubmittingReply)
      return;

    try {
      setIsSubmittingReply(true);
      await discussionService.addReply(selectedDiscussion._id, replyContent);
      setReplyContent("");
      showSuccess("Reply posted successfully");

      // Refresh discussion details
      const response = (await discussionService.getDiscussionById(
        selectedDiscussion._id,
      )) as any;
      if (response && response.discussion) {
        setSelectedDiscussion(response.discussion);
      }
      // Also refresh the main list to update reply counts
      fetchDiscussions();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to post reply";
      showError(errorMessage);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    if (!window.confirm("Are you sure you want to delete this reply?")) return;

    try {
      await discussionService.deleteReply(replyId);
      showSuccess("Reply deleted successfully");

      // Refresh discussion details
      if (selectedDiscussion) {
        const response = (await discussionService.getDiscussionById(
          selectedDiscussion._id,
        )) as any;
        if (response && response.discussion) {
          setSelectedDiscussion(response.discussion);
        }
      }
      // Refresh total list
      fetchDiscussions();
    } catch (error) {
      showError("Failed to delete reply");
    }
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#102040]">
            Discussions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage user discussions, questions, and answers across all courses.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4">
          <div className="w-full">
            <Input
              placeholder="Search discussions..."
              icon={<Search size={20} />}
              className="bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="h-[39px] px-3 border-dashed"
                onClick={() => {
                  setIsStatusOpen(!isStatusOpen);
                  setIsSortOpen(false);
                }}
              >
                <Filter className="mr-2 h-4 w-4" />
                Status:{" "}
                {statusOptions.find((o) => o.value === statusFilter)?.label}
              </Button>
              {isStatusOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsStatusOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[120px]">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setStatusFilter(option.value);
                          setIsStatusOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          statusFilter === option.value
                            ? "bg-gray-100 font-bold"
                            : ""
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Course Filter */}
            <div className="relative">
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-600 font-medium h-11"
                onClick={() => {
                  setIsCourseFilterOpen(!isCourseFilterOpen);
                  setIsStatusOpen(false); // Close other filters
                  setIsSortOpen(false); // Close other filters
                }}
              >
                <BookOpen size={16} className="mr-2 text-gray-400" />
                {courseFilter === "all"
                  ? "All Courses"
                  : courses.find((c) => c._id === courseFilter)?.title ||
                    "Course"}
              </Button>

              {isCourseFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => {
                      setIsCourseFilterOpen(false);
                      setCourseSearchTerm("");
                    }}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[250px] max-h-80 flex flex-col">
                    <div className="p-2 border-b border-gray-100 mb-1">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
                        <Input
                          placeholder="Search courses..."
                          value={courseSearchTerm}
                          onChange={(e) => setCourseSearchTerm(e.target.value)}
                          className="pl-8 h-9 text-xs"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="overflow-y-auto flex-1 max-h-60">
                      <button
                        onClick={() => {
                          setCourseFilter("all");
                          setIsCourseFilterOpen(false);
                          setCourseSearchTerm("");
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 mb-1 ${
                          courseFilter === "all" ? "bg-gray-100 font-bold" : ""
                        }`}
                      >
                        All Courses
                      </button>

                      {courses
                        .filter((c) =>
                          c.title
                            .toLowerCase()
                            .includes(courseSearchTerm.toLowerCase()),
                        )
                        .map((course) => (
                          <button
                            key={course._id}
                            onClick={() => {
                              setCourseFilter(course._id);
                              setIsCourseFilterOpen(false);
                              setCourseSearchTerm("");
                              setCurrentPage(1);
                            }}
                            className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                              courseFilter === course._id
                                ? "bg-gray-100 font-bold"
                                : ""
                            }`}
                          >
                            {course.title}
                          </button>
                        ))}

                      {courses.filter((c) =>
                        c.title
                          .toLowerCase()
                          .includes(courseSearchTerm.toLowerCase()),
                      ).length === 0 && (
                        <div className="p-3 text-xs text-center text-gray-500 italic">
                          No courses found matching "{courseSearchTerm}"
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Pinned Filter */}
            <div className="relative">
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-600 font-medium h-11"
                onClick={() => {
                  setIsPinnedFilterOpen(!isPinnedFilterOpen);
                  setIsStatusOpen(false);
                  setIsCourseFilterOpen(false);
                  setIsLockedFilterOpen(false);
                  setIsSortOpen(false);
                }}
              >
                <Pin size={16} className="mr-2 text-gray-400" />
                {pinnedOptions.find((opt) => opt.value === pinnedFilter)
                  ?.label || "All"}
              </Button>

              {isPinnedFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsPinnedFilterOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[160px]">
                    {pinnedOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setPinnedFilter(opt.value);
                          setIsPinnedFilterOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          pinnedFilter === opt.value
                            ? "bg-gray-100 font-bold"
                            : ""
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Locked Filter */}
            <div className="relative">
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-600 font-medium h-11"
                onClick={() => {
                  setIsLockedFilterOpen(!isLockedFilterOpen);
                  setIsStatusOpen(false);
                  setIsCourseFilterOpen(false);
                  setIsPinnedFilterOpen(false);
                  setIsSortOpen(false);
                }}
              >
                <Lock size={16} className="mr-2 text-gray-400" />
                {lockedOptions.find((opt) => opt.value === lockedFilter)
                  ?.label || "All"}
              </Button>

              {isLockedFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsLockedFilterOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[160px]">
                    {lockedOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setLockedFilter(opt.value);
                          setIsLockedFilterOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          lockedFilter === opt.value
                            ? "bg-gray-100 font-bold"
                            : ""
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="h-[39px] px-3 border-dashed"
                onClick={() => {
                  setIsSortOpen(!isSortOpen);
                  setIsStatusOpen(false);
                }}
              >
                <ArrowUpDown className="mr-2 h-4 w-4" />
                Sort: {sortOptions.find((o) => o.value === sortBy)?.label}
              </Button>
              {isSortOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsSortOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[150px]">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value);
                          setIsSortOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          sortBy === option.value ? "bg-gray-100 font-bold" : ""
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Table Rendering */}
        <Card className="p-0 border shadow-sm overflow-visible bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 font-semibold text-gray-900 w-[35%]">
                    Discussion
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900 w-[20%]">
                    Author
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900 w-[25%]">
                    Context (Course/Lesson)
                  </th>
                  <th className="px-6 py-4 font-semibold text-gray-900 w-[15%]">
                    Stats
                  </th>
                  <th className="px-6 py-4 w-[5%]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-24 text-center text-gray-500"
                    >
                      Loading discussions...
                    </td>
                  </tr>
                ) : discussions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-32 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <MessageCircle className="h-12 w-12 mb-2 text-gray-300" />
                        <p className="text-lg font-medium">
                          No discussions found
                        </p>
                        <p className="text-sm">
                          Try adjusting your filters or search terms
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  discussions.map((discussion) => (
                    <tr
                      key={discussion._id}
                      className="hover:bg-gray-50/50 transition-colors group"
                    >
                      <td className="px-6 py-5 align-top">
                        <div className="space-y-1">
                          <div className="flex flex-wrap gap-2 mb-2">
                            {discussion.isPinned && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">
                                <Pin className="h-3 w-3 mr-1" /> Pinned
                              </span>
                            )}
                            {discussion.isLocked && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-red-100 text-red-700 border border-red-200">
                                <Lock className="h-3 w-3 mr-1" /> Locked
                              </span>
                            )}
                            {discussion.isSolved && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-green-100 text-green-700 border border-green-200">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Solved
                              </span>
                            )}
                          </div>
                          <p className="font-semibold text-[#102040] line-clamp-2">
                            {discussion.title}
                          </p>
                          <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                            {discussion.content}
                          </p>
                          <div className="text-[11px] text-gray-400 pt-2 font-medium">
                            {format(
                              new Date(discussion.createdAt),
                              "MMM d, yyyy",
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 align-top">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-700 font-bold text-xs border border-indigo-100 shadow-sm">
                            {discussion.author.firstName?.[0]}
                            {discussion.author.lastName?.[0]}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#102040] truncate">
                              {discussion.author.firstName}{" "}
                              {discussion.author.lastName}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              @{discussion.author.username}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 align-top">
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-sm font-medium text-[#102040]">
                            <BookOpen className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            <span className="line-clamp-1">
                              {discussion.course?.title || "Unknown Course"}
                            </span>
                          </div>
                          {discussion.lesson && (
                            <div className="flex items-center gap-2 text-xs text-gray-500 pl-5">
                              <FileText className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                              <span className="line-clamp-1">
                                {discussion.lesson.title}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5 align-top">
                        <div className="flex items-center gap-4 text-[13px] text-gray-600">
                          <div
                            className="flex items-center gap-1.5"
                            title="Upvotes"
                          >
                            <ThumbsUp className="h-4 w-4 text-gray-400" />
                            <span>{discussion.upvotes?.length || 0}</span>
                          </div>
                          <div
                            className="flex items-center gap-1.5"
                            title="Replies"
                          >
                            <MessageCircle className="h-4 w-4 text-gray-400" />
                            <span>{discussion.replies?.length || 0}</span>
                          </div>
                          <div
                            className="flex items-center gap-1.5"
                            title="Views"
                          >
                            <Eye className="h-4 w-4 text-gray-400" />
                            <span>{discussion.views || 0}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-right relative align-top">
                        <button
                          className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeMenu?.id === discussion._id) {
                              setActiveMenu(null);
                            } else {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setActiveMenu({
                                id: discussion._id,
                                position: {
                                  top: rect.bottom,
                                  right: window.innerWidth - rect.right,
                                },
                              });
                            }
                          }}
                        >
                          <MoreHorizontal size={20} />
                        </button>
                        <TableActionMenu
                          isOpen={activeMenu?.id === discussion._id}
                          onClose={() => setActiveMenu(null)}
                          position={activeMenu?.position || null}
                        >
                          <button
                            onClick={() => {
                              handleViewDetails(discussion._id);
                              setActiveMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <ExternalLink
                              size={14}
                              className="text-indigo-600"
                            />
                            View Details
                          </button>
                          <div className="h-px bg-gray-100 my-1" />
                          <button
                            onClick={() => {
                              handleTogglePin(discussion);
                              setActiveMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            {discussion.isPinned ? (
                              <PinOff size={14} className="text-yellow-600" />
                            ) : (
                              <Pin size={14} className="text-yellow-600" />
                            )}
                            {discussion.isPinned
                              ? "Unpin Discussion"
                              : "Pin Discussion"}
                          </button>
                          <button
                            onClick={() => {
                              handleToggleLock(discussion);
                              setActiveMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            {discussion.isLocked ? (
                              <Unlock size={14} className="text-blue-600" />
                            ) : (
                              <Lock size={14} className="text-blue-600" />
                            )}
                            {discussion.isLocked
                              ? "Unlock Discussion"
                              : "Lock Discussion"}
                          </button>
                          <button
                            onClick={() => {
                              handleToggleSolved(discussion);
                              setActiveMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            {discussion.isSolved ? (
                              <ArrowUpDown
                                size={14}
                                className="text-green-600"
                              />
                            ) : (
                              <CheckCircle2
                                size={14}
                                className="text-green-600"
                              />
                            )}
                            {discussion.isSolved
                              ? "Mark as Unsolved"
                              : "Mark as Solved"}
                          </button>
                          <div className="h-px bg-gray-100 my-1" />
                          <button
                            onClick={() => {
                              handleDelete(discussion._id);
                              setActiveMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 size={14} />
                            Delete Discussion
                          </button>
                        </TableActionMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 text-sm text-gray-500 bg-white">
            <span>
              {totalItems > 0 ? (
                <>
                  Showing{" "}
                  {Math.min((currentPage - 1) * pageSize + 1, totalItems)} to{" "}
                  {Math.min(currentPage * pageSize, totalItems)} of {totalItems}{" "}
                  discussions
                </>
              ) : (
                "No discussions found"
              )}
            </span>
            <div className="flex gap-1">
              <button
                className="px-3 py-1.5 hover:bg-gray-100 rounded-md border border-gray-200 disabled:opacity-40 disabled:hover:bg-white transition-colors"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p = i + 1;
                if (totalPages > 5) {
                  if (currentPage <= 3) p = i + 1;
                  else if (currentPage >= totalPages - 2)
                    p = totalPages - 4 + i;
                  else p = currentPage - 2 + i;
                }
                if (p > totalPages) return null;
                return (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`min-w-[32px] px-2 py-1.5 rounded-md border transition-colors ${
                      currentPage === p
                        ? "bg-indigo-600 text-white border-indigo-600 font-semibold"
                        : "border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}

              <button
                className="px-3 py-1.5 hover:bg-gray-100 rounded-md border border-gray-200 disabled:opacity-40 disabled:hover:bg-white transition-colors"
                disabled={currentPage === totalPages || totalItems === 0}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Discussion Detail Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDiscussion(null);
        }}
        title="Discussion Details"
        className="max-w-4xl"
      >
        {isLoadingDetails ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading discussion details...</div>
          </div>
        ) : selectedDiscussion ? (
          <div className="space-y-6">
            {/* Discussion Header */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  {selectedDiscussion.isPinned && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">
                      <Pin className="h-3 w-3 mr-1" /> Pinned
                    </span>
                  )}
                  {selectedDiscussion.isLocked && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                      <Lock className="h-3 w-3 mr-1" /> Locked
                    </span>
                  )}
                  {selectedDiscussion.isSolved && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Solved
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTogglePin(selectedDiscussion)}
                    className="h-8 text-xs gap-1"
                  >
                    {selectedDiscussion.isPinned ? (
                      <>
                        <PinOff className="h-3 w-3" /> Unpin
                      </>
                    ) : (
                      <>
                        <Pin className="h-3 w-3" /> Pin
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleLock(selectedDiscussion)}
                    className="h-8 text-xs gap-1"
                  >
                    {selectedDiscussion.isLocked ? (
                      <>
                        <Unlock className="h-3 w-3" /> Unlock
                      </>
                    ) : (
                      <>
                        <Lock className="h-3 w-3" /> Lock
                      </>
                    )}
                  </Button>
                  <Button
                    variant={selectedDiscussion.isSolved ? "ghost" : "outline"}
                    size="sm"
                    onClick={() => handleToggleSolved(selectedDiscussion)}
                    className={`h-8 text-xs gap-1 ${
                      selectedDiscussion.isSolved
                        ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                        : "border-green-200 text-green-700 hover:bg-green-50"
                    }`}
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    {selectedDiscussion.isSolved
                      ? "Mark Unsolved"
                      : "Mark Solved"}
                  </Button>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-[#102040]">
                {selectedDiscussion.title}
              </h3>

              {/* Author and Meta */}
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-700 font-bold text-xs border border-indigo-100">
                    {selectedDiscussion.author.firstName?.[0]}
                    {selectedDiscussion.author.lastName?.[0]}
                  </div>
                  <span className="font-medium text-[#102040]">
                    {selectedDiscussion.author.firstName}{" "}
                    {selectedDiscussion.author.lastName}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {format(
                      new Date(selectedDiscussion.createdAt),
                      "MMM d, yyyy 'at' h:mm a",
                    )}
                  </span>
                </div>
              </div>

              {/* Course/Lesson Context */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700">
                    {selectedDiscussion.course?.title || "Unknown Course"}
                  </span>
                </div>
                {selectedDiscussion.lesson && (
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-700">
                      {selectedDiscussion.lesson.title}
                    </span>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6 text-sm text-gray-600 pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <ThumbsUp className="h-4 w-4 text-gray-400" />
                  <span>{selectedDiscussion.upvotes?.length || 0} upvotes</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-gray-400" />
                  <span>{selectedDiscussion.replies?.length || 0} replies</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-gray-400" />
                  <span>{selectedDiscussion.views || 0} views</span>
                </div>
              </div>
            </div>

            {/* Discussion Content */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Question
              </h4>
              <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                {selectedDiscussion.content}
              </p>
            </div>

            {/* Replies Section */}
            {selectedDiscussion.replies &&
              selectedDiscussion.replies.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-bold text-[#102040] flex items-center gap-2">
                    <MessageCircle className="h-5 w-5" />
                    Replies ({selectedDiscussion.replies.length})
                  </h4>
                  <div className="space-y-4">
                    {selectedDiscussion.replies.map(
                      (reply: any, index: number) => (
                        <div
                          key={reply._id || index}
                          className="bg-white rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-700 font-bold text-xs border border-blue-100 shrink-0">
                              {reply.author?.firstName?.[0] || "U"}
                              {reply.author?.lastName?.[0] || ""}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-sm text-[#102040]">
                                    {reply.author?.firstName || "Unknown"}{" "}
                                    {reply.author?.lastName || "User"}
                                  </span>
                                  {reply.isSolution && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                                      <CheckCircle2 className="h-3 w-3 mr-1" />{" "}
                                      Solution
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {format(
                                    new Date(reply.createdAt),
                                    "MMM d, yyyy",
                                  )}
                                </span>
                              </div>
                              <p className="text-gray-700 text-sm whitespace-pre-wrap leading-relaxed mb-2">
                                {reply.content}
                              </p>
                              <div className="flex items-center justify-between mt-2">
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <ThumbsUp className="h-3 w-3" />
                                  <span>{reply.upvotes?.length || 0}</span>
                                </div>
                                <button
                                  onClick={() => handleDeleteReply(reply._id)}
                                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                  title="Delete Reply"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

            {selectedDiscussion.replies &&
              selectedDiscussion.replies.length === 0 && (
                <div className="text-center py-8 text-gray-500 border-t border-gray-100">
                  <MessageCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No replies yet</p>
                </div>
              )}

            {/* Post Reply Form */}
            {!selectedDiscussion.isLocked ? (
              <div className="pt-6 border-t border-gray-100">
                <form onSubmit={handleAddReply} className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-700">
                    Post a Reply
                  </h4>
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Write your reply here..."
                    className="w-full min-h-[120px] p-4 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-y text-sm"
                    required
                  />
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={isSubmittingReply || !replyContent.trim()}
                      className="px-6"
                    >
                      {isSubmittingReply ? "Posting..." : "Post Reply"}
                    </Button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-center justify-center gap-2 text-red-600 text-sm font-medium">
                <Lock className="h-4 w-4" />
                <span>
                  This discussion is locked. No new replies can be added.
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No discussion selected</p>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
